import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, Check, X, Flag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import StatusBadge, { VerificationBadge } from "@/components/site/StatusBadge";
import MultilingualText from "@/components/ui/MultilingualText";
import { toast } from "sonner";

/** /app/moderation — admin queue: unreviewed cases, unverified community updates, abuse reports. */
export default function Moderation() {
  const { user } = useAuth();
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/missing/admin/queue");
      setQueue(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function act(caseId, action, updateId) {
    try {
      await api.post(`/v1/missing/${caseId}/moderate`, { action, update_id: updateId });
      toast.success("Done.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (user && user.role !== "admin") {
    return (
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-24 text-center" data-testid="moderation-denied">
        <ShieldCheck size={28} className="mx-auto text-[color:var(--sepia)]" />
        <h1 className="heritage-title text-3xl mt-4">Moderators only</h1>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-6 md:px-10 pt-8 pb-24" data-testid="moderation-page">
      <header className="mb-10">
        <div className="overline mb-2">§ Moderation</div>
        <h1 className="heritage-title text-4xl md:text-5xl leading-tight">The review desk</h1>
        <p className="mt-3 text-[color:var(--ink-soft)]">
          New cases go public immediately and land here for review. Verify what is true, hide what is not safe.
        </p>
      </header>

      {loading || !queue ? (
        <div className="py-16 text-center text-[color:var(--ink-soft)]">Gathering the queue…</div>
      ) : (
        <div className="space-y-14">
          {/* Unreviewed cases */}
          <div data-testid="moderation-cases">
            <div className="overline text-[color:var(--sepia)] mb-4">Cases awaiting review · {queue.cases.length}</div>
            {queue.cases.length === 0 ? (
              <p className="font-italic-serif italic text-[color:var(--ink-soft)]">The desk is clear.</p>
            ) : (
              <div className="space-y-4">
                {queue.cases.map((c) => (
                  <div key={c.case_id} className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`mod-case-${c.case_id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={c.status} />
                        <span className="numeric-index text-[color:var(--ink-soft)]">{c.case_id}</span>
                        {c.is_minor && <span className="border border-[color:var(--rust)] px-1.5 py-px text-[10px] font-heritage-caps text-[color:var(--rust)]">MINOR</span>}
                      </div>
                      <Link to={`/missing/case/${c.case_id}`} className="mt-1 block font-serif-display text-xl link-underline">{c.name}</Link>
                      <div className="text-xs text-[color:var(--ink-soft)]">
                        {[c.loc?.city, c.loc?.region, c.loc?.country].filter(Boolean).join(", ")} · {c.last_seen_date}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => act(c.case_id, "approve")} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`mod-approve-${c.case_id}`}>
                        <Check size={12} /> Approve
                      </button>
                      <button
                        onClick={() => act(c.case_id, c.visibility === "public" ? "unpublish" : "republish")}
                        className="btn-ghost !py-1.5 !px-3 !text-xs"
                        data-testid={`mod-unpublish-${c.case_id}`}
                      >
                        <EyeOff size={12} /> {c.visibility === "public" ? "Unpublish" : "Republish"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unverified community updates */}
          <div data-testid="moderation-updates">
            <div className="overline text-[color:var(--sepia)] mb-4">Community updates awaiting verification · {queue.updates.length}</div>
            {queue.updates.length === 0 ? (
              <p className="font-italic-serif italic text-[color:var(--ink-soft)]">Nothing waiting.</p>
            ) : (
              <div className="space-y-4">
                {queue.updates.map((u) => (
                  <div key={u.id} className="border border-[color:var(--sepia)]/25 p-5" data-testid={`mod-update-${u.id}`}>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-soft)]">
                      <span>{u.author_name}</span>
                      <VerificationBadge verification={u.verification} />
                      <span>{u.created_at && new Date(u.created_at).toLocaleString()}</span>
                    </div>
                    <MultilingualText value={u.body} className="mt-2 text-[15px] leading-[1.6]" />
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => act(u.case_code, "verify_update", u.id)} className="btn-ghost !py-1 !px-3 !text-xs" data-testid={`mod-verify-${u.id}`}>
                        <Check size={11} /> Verify
                      </button>
                      <button onClick={() => act(u.case_code, "disprove_update", u.id)} className="btn-ghost !py-1 !px-3 !text-xs" data-testid={`mod-disprove-${u.id}`}>
                        <X size={11} /> Disprove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Abuse reports */}
          <div data-testid="moderation-reports">
            <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
              <Flag size={12} /> Abuse &amp; scam reports · {queue.reports.length}
            </div>
            {queue.reports.length === 0 ? (
              <p className="font-italic-serif italic text-[color:var(--ink-soft)]">No reports.</p>
            ) : (
              <div className="space-y-3">
                {queue.reports.map((r) => (
                  <div key={r.id} className="border-l-2 border-[color:var(--rust)] pl-4 py-1" data-testid={`mod-report-${r.id}`}>
                    <div className="numeric-index text-[color:var(--ink-soft)]">
                      {r.created_at && new Date(r.created_at).toLocaleString()}
                    </div>
                    <p className="mt-1 text-[15px]">{r.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
